/**
 * Direct Project Update
 * This script directly updates projects with the correct JARVIS UUIDs
 */

import { directSupabase } from '../lib/supabaseClient';

export async function directProjectUpdate() {
  console.log('🔧 Starting direct project update...');
  
  try {
    // Step 1: Get all mappings
    console.log('📊 Fetching mappings...');
    const { data: mappings, error: mappingError } = await directSupabase
      .from('customer_migration_mapping')
      .select('quotedock_customer_id, jarvis_customer_id, customer_name');
    
    if (mappingError) {
      throw new Error(`Failed to fetch mappings: ${mappingError.message}`);
    }
    
    console.log(`Found ${mappings?.length || 0} mappings`);
    
    // Step 2: Get all projects that need updating
    console.log('📊 Fetching projects that need updating...');
    const { data: projects, error: projectError } = await directSupabase
      .from('projects')
      .select('id, project_name, customer_id')
      .not('customer_id', 'is', null);
    
    if (projectError) {
      throw new Error(`Failed to fetch projects: ${projectError.message}`);
    }
    
    console.log(`Found ${projects?.length || 0} projects with customer_id`);
    
    // Step 3: Process each project individually
    let updatedCount = 0;
    const results = [];
    
    for (const project of projects || []) {
      try {
        // Convert customer_id to number for comparison (in case it's stored as string)
        const projectCustomerId = Number(project.customer_id);
        
        // Skip if customer_id is already a UUID (36 characters with dashes)
        if (typeof project.customer_id === 'string' && 
            project.customer_id.length === 36 && 
            project.customer_id.includes('-')) {
          console.log(`⏭️ Skipping ${project.project_name} - already has UUID`);
          results.push({
            project: project.project_name,
            status: 'skipped',
            reason: 'Already UUID'
          });
          continue;
        }
        
        // Find matching mapping
        const mapping = mappings?.find(m => m.quotedock_customer_id === projectCustomerId);
        
        if (!mapping) {
          console.log(`⚠️ No mapping found for project "${project.project_name}" (customer_id: ${projectCustomerId})`);
          results.push({
            project: project.project_name,
            customerId: projectCustomerId,
            status: 'unmapped',
            reason: 'No mapping found'
          });
          continue;
        }
        
        // Update project with JARVIS UUID
        console.log(`🔄 Updating "${project.project_name}" (${mapping.customer_name}): ${projectCustomerId} → ${mapping.jarvis_customer_id}`);
        
        const { error: updateError } = await directSupabase
          .from('projects')
          .update({ customer_id: mapping.jarvis_customer_id })
          .eq('id', project.id);
        
        if (updateError) {
          console.error(`❌ Failed to update project "${project.project_name}":`, updateError);
          results.push({
            project: project.project_name,
            customerId: projectCustomerId,
            customerName: mapping.customer_name,
            status: 'failed',
            error: updateError.message
          });
        } else {
          updatedCount++;
          console.log(`✅ Updated "${project.project_name}" → ${mapping.customer_name}`);
          results.push({
            project: project.project_name,
            customerId: projectCustomerId,
            customerName: mapping.customer_name,
            jarvisUuid: mapping.jarvis_customer_id,
            status: 'success'
          });
        }
        
      } catch (error) {
        console.error(`❌ Error processing project "${project.project_name}":`, error);
        results.push({
          project: project.project_name,
          status: 'error',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    
    // Step 4: Summary
    console.log('✅ Direct project update completed!');
    console.log(`📊 Summary:`);
    console.log(`   - Total projects processed: ${projects?.length || 0}`);
    console.log(`   - Successfully updated: ${updatedCount}`);
    console.log(`   - Failed: ${results.filter(r => r.status === 'failed').length}`);
    console.log(`   - Unmapped: ${results.filter(r => r.status === 'unmapped').length}`);
    console.log(`   - Already UUID: ${results.filter(r => r.status === 'skipped').length}`);
    
    // Show failed updates if any
    const failed = results.filter(r => r.status === 'failed');
    if (failed.length > 0) {
      console.log('❌ Failed updates:');
      console.table(failed);
    }
    
    // Show unmapped projects if any
    const unmapped = results.filter(r => r.status === 'unmapped');
    if (unmapped.length > 0) {
      console.log('⚠️ Unmapped projects (these will remain with NULL customer_id):');
      console.table(unmapped);
    }
    
    return {
      success: true,
      totalProjects: projects?.length || 0,
      updated: updatedCount,
      failed: failed.length,
      unmapped: unmapped.length,
      skipped: results.filter(r => r.status === 'skipped').length,
      results
    };
    
  } catch (error) {
    console.error('❌ Direct project update failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).directUpdate = directProjectUpdate;
















